import { emailService } from '@/services/email/emailService';
import { generateInvoicePDF } from './invoicePdfService';
import { sendInvoice } from './invoiceService';
import { logError } from '@/lib/error-handler';
import type { Invoice } from '@/types/invoice.types';

export interface InvoiceEmailOptions {
  to: string | string[];
  cc?: string | string[];
  subject?: string;
  message?: string;
}

export async function sendInvoiceEmail(
  invoice: Invoice,
  options: InvoiceEmailOptions
): Promise<{ success: boolean; error?: string }> {
  try {
    // Generate PDF
    const pdfBlob = await generateInvoicePDF(invoice);
    
    // Convert blob to base64 for email attachment
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve) => {
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]); // Remove data:application/pdf;base64, prefix
      };
    });
    reader.readAsDataURL(pdfBlob);
    const pdfBase64 = await base64Promise;

    // Default subject if not provided
    const subject = options.subject || `Invoice ${invoice.invoice_number} from ${invoice.organization?.name || 'Spherical Manager'}`;
    
    // Default message if not provided
    const defaultMessage = `Dear ${invoice.bill_to_name || 'Customer'},

Please find attached invoice ${invoice.invoice_number} for your review.

Invoice Details:
- Amount: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(invoice.total_amount)}
- Due Date: ${new Date(invoice.due_date).toLocaleDateString()}

${invoice.payment_terms ? `Payment Terms: ${invoice.payment_terms}` : ''}

${options.message || 'Thank you for your business!'}

Best regards,
${invoice.organization?.name || 'Spherical Manager Team'}`;

    // Prepare HTML version
    const htmlMessage = defaultMessage.replace(/\n/g, '<br>');

    // Send email
    const result = await emailService.send({
      to: options.to,
      cc: options.cc,
      subject,
      text: defaultMessage,
      html: `
        <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          ${htmlMessage}
        </div>
      `,
      attachments: [
        {
          filename: `invoice-${invoice.invoice_number}.pdf`,
          content: pdfBase64,
          contentType: 'application/pdf',
        },
      ],
    });

    if (result.success) {
      // Update invoice status to 'sent' if it's currently 'draft'
      if (invoice.status === 'draft') {
        await sendInvoice(invoice.id);
      }
    }

    return result;
  } catch (error) {
    logError('Failed to send invoice email', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send invoice email',
    };
  }
}

export async function sendInvoiceReminder(
  invoice: Invoice,
  options: Omit<InvoiceEmailOptions, 'subject' | 'message'>
): Promise<{ success: boolean; error?: string }> {
  const daysOverdue = Math.floor(
    (Date.now() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24)
  );

  const reminderMessage = `This is a friendly reminder that invoice ${invoice.invoice_number} is ${
    daysOverdue > 0 ? `${daysOverdue} days overdue` : 'due soon'
  }.

We would appreciate your prompt attention to this matter.

If you have already sent payment, please disregard this reminder. If you have any questions or concerns, please don't hesitate to contact us.`;

  return sendInvoiceEmail(invoice, {
    ...options,
    subject: `Payment Reminder: Invoice ${invoice.invoice_number}`,
    message: reminderMessage,
  });
}